import React from "react";
import { FormattedHTMLMessage } from "react-intl";
import store from "store";

import {
    availableLanguagesForTranslation,
    languages,
    translationLevelStatusLabels,
} from "../../../../common/parameters";
import { LoaderWrapper, Loader } from "../../../components/Loader";
import { Select } from "../../../components/Form";
import routing from "../../../routing";

import TranslatorChat from "./TranslatorChat";
import TokenTable from "./TokenTable";

import { Block, BlockHeader, Caret, LevelName, StatusMarker, BlockContent, MergeButton } from "./styles";
import { IProps } from "./interfaces";

class LanguagePage extends React.Component<IProps, any> {
    state = {
        users: [],
        tokenLevels: [],
        translationLevelStatus: [],
        isInitalFetching: true,
        errorInitialLoad: false,
        progress: [],
        chatCheckedAt: null,
        sourceLanguage: "en",
    };
    eventSource: EventSource;
    chatCallback: (data: any) => void;
    componentDidMount() {
        this.getLevels();
        this.setState({
            sourceLanguage: store.get("sourceLanguage") || "en",
        });
        this.eventSource = new EventSource("/api/translation/broadcasting");
        this.eventSource.onmessage = this.handleBroadcasting;
    }
    setEventSourceCallback = (callback) => {
        this.chatCallback = callback;
    };
    componentWillUnmount() {
        this.eventSource.close();
    }
    setChatChecked = (chatCheckedAt: number) => {
        this.setState({ chatCheckedAt });
    };
    handleBroadcasting = (message: any) => {
        const { data } = JSON.parse(message.data);
        if (!data) return;
        if (data.user === this.props.user._id || data.language !== this.props.match.params.language) return;
        if (data.event === "edit-translation") {
            this.setState(({ tokenLevels }) => ({
                tokenLevels: tokenLevels.map(level => {
                    return {
                        ...level,
                        ...(level.tokens && {
                            tokens: level.tokens.map(token => ({
                                ...token,
                                translationToken: token._id === data._id ? data.token : token.translationToken,
                            })),
                        }),
                    };
                }),
            }));
        } else {
            if (this.chatCallback) {
                this.chatCallback(data);
            }
        }
    };
    goBack = () => {
        this.props.history.replace(`/${routing.translate}`);
    };
    getLevels = () => {
        const { language } = this.props.match.params;
        if (!language || availableLanguagesForTranslation.indexOf(language) === -1) {
            this.goBack();
        }
        this.props.get(
            `translation/get-translation-levels/${language}`,
            ({ data }) => {
                this.setState({
                    ...data,
                    isInitalFetching: false,
                });
            },
            error => {
                if ((error as any).response.status === 403) this.goBack();
            }
        );
    };
    getTokenLevel = loadingToken => {
        this.props.get(
            `translation/translation-level-tokens/${this.props.match.params.language}/${loadingToken._id}/${this.state.sourceLanguage}`,
            res => {
                const { data } = res;
                this.setState(({ tokenLevels }) => ({
                    tokenLevels: tokenLevels.map(level => {
                        if (level._id === loadingToken._id) {
                            return {
                                ...level,
                                tokens: data.tokens.map(token => ({
                                    ...token,
                                    translationToken:
                                        data.translationTokens.find(ttoken => ttoken.tokenId === token._id) || null,
                                })),
                                isFetching: false,
                            };
                        }
                        return level;
                    }),
                }));
            }
        );
    };
    toggleLevel = togglingLevel => {
        const { translationLevelStatus } = this.state;
        const status =
            (translationLevelStatus.find(status => status.level === togglingLevel._id) || { status: 0 }).status || 0;
        if (!status && this.props.user.permission < 3) {
            return;
        }
        this.setState(({ tokenLevels }) => ({
            tokenLevels: tokenLevels.map(level => {
                if (level._id === togglingLevel._id) {
                    if (level.isOpen) {
                        return {
                            ...level,
                            isOpen: false,
                        };
                    } else {
                        this.getTokenLevel(level);
                        return {
                            ...level,
                            isFetching: true,
                            isOpen: true,
                        };
                    }
                }
                return level;
            }),
        }));
    };
    saveToken = (savingToken, value) => {
        return new Promise(resolve => {
            this.props.post(
                `translation/edit-translation/${this.props.match.params.language}`,
                {
                    _id: savingToken._id,
                    value,
                },
                res => {
                    this.setState(
                        ({ tokenLevels }) => ({
                            tokenLevels: tokenLevels.map(level => {
                                return {
                                    ...level,
                                    ...(level.tokens && {
                                        tokens: level.tokens.map(token => ({
                                            ...token,
                                            translationToken:
                                                token._id === savingToken._id
                                                    ? res.data.translationToken
                                                    : token.translationToken,
                                        })),
                                    }),
                                };
                            }),
                        }),
                        () => {
                            resolve(true);
                        }
                    );
                },
                () => {
                    resolve(true);
                }
            );
        });
    };
    getToken = async refreshingToken => {
        this.props.get(
            `translation/token-translation/${this.props.match.params.language}/${refreshingToken._id}`,
            res => {
                this.setState(({ tokenLevels }) => ({
                    tokenLevels: tokenLevels.map(level => {
                        return {
                            ...level,
                            ...(level.tokens && {
                                tokens: level.tokens.map(token => ({
                                    ...token,
                                    translationToken:
                                        token._id === refreshingToken._id
                                            ? res.data.translationToken
                                            : token.translationToken,
                                })),
                            }),
                        };
                    }),
                }));
            }
        );
    };
    approveTranslation = approvingToken => {
        this.props.post(
            `translation/approve-translation/${this.props.match.params.language}`,
            { _id: approvingToken._id },
            res => {
                this.setState(({ tokenLevels }) => ({
                    tokenLevels: tokenLevels.map(level => {
                        return {
                            ...level,
                            ...(level.tokens && {
                                tokens: level.tokens.map(token => ({
                                    ...token,
                                    translationToken:
                                        token._id === approvingToken._id
                                            ? res.data.translationToken
                                            : token.translationToken,
                                })),
                            }),
                        };
                    }),
                }));
            }
        );
    };

    mergeLevel = (e, level) => {
        e.stopPropagation();
        this.props.post(
            `translation/merge/${this.props.match.params.language}`,
            {
                level: level._id,
            },
            () => {
                this.getTokenLevel(level);
            }
        );
    };

    changeSourceLanguage = ({ target: { value } }) => {
        store.set("sourceLanguage", value);
        this.setState(
            {
                sourceLanguage: value,
                tokenLevels: [],
                isInitalFetching: true,
            },
            this.getLevels
        );
    };

    handleLevelStatusChange = (e, _id, status) => {
        const {
            user,
        } = this.props;
        const { language } = this.props.match.params;
        if (user.permission > 2) {
            e.stopPropagation();
            this.props.post("set-token-level-status", {
                language,
                tokenLevel: _id,
                status: (status + 1) % 5,
            }, () => {
                this.setState({ translationLevelStatus: 
                    this.state.translationLevelStatus.map(_status => {
                        if (_status.level === _id) {
                            return {
                                ..._status,
                                status: (status + 1) % 5,
                            };
                        }
                        return _status;
                    })
                })
            })
        }
    }
    render() {
        const {
            intl: { formatMessage },
            user,
        } = this.props;
        const {
            isInitalFetching,
            tokenLevels,
            translationLevelStatus,
            users,
            progress,
            chatCheckedAt,
            sourceLanguage,
        } = this.state;
        const { language } = this.props.match.params;
        return (
            <div>
                <h1>{languages(formatMessage).find(({ value }) => value === language)?.label}</h1>
                <div>
                    <FormattedHTMLMessage id="translate.work.description" />
                </div>
                <Select
                    name="sourceLanguage"
                    label="Source language"
                    value={sourceLanguage}
                    options={[{ value: "en", label: "English" }, ...languages(formatMessage)]}
                    onChange={this.changeSourceLanguage}
                />
                {users.length > 0 && (
                    <TranslatorChat
                        setChatChecked={this.setChatChecked}
                        language={language}
                        users={users}
                        setEventSourceCallback={this.setEventSourceCallback}
                        chatCheckedAt={chatCheckedAt}
                    />
                )}
                {isInitalFetching && (
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                )}

                {!isInitalFetching && (
                    <div>
                        {tokenLevels.map(level => {
                            const status =
                                (translationLevelStatus.find(status => status.level === level._id) || { status: 0 })
                                    .status || 0;
                            return (
                                <Block key={level._id}>
                                    <BlockHeader
                                        onClick={() => this.toggleLevel(level)}
                                        isOpen={level.isOpen}
                                        style={{ cursor: !status ? "default" : "" }}
                                    >
                                        <LevelName>
                                            {level.name}{" "}
                                            {level.tokens ? (
                                                <>
                                                    ({level.tokens.filter(token => token.translationToken).length}/
                                                    {
                                                        level.tokens.filter(
                                                            token =>
                                                                token.translationToken &&
                                                                token.translationToken.approvedBy.length > 0
                                                        ).length
                                                    }
                                                    /{level.tokens.length})
                                                </>
                                            ) : (
                                                <>
                                                    ({progress.find(pr => pr._id === level._id).edited}/
                                                    {progress.find(pr => pr._id === level._id).approved}/
                                                    {progress.find(pr => pr._id === level._id).total}
                                                    {user.permission > 2 && progress.find(pr => pr._id === level._id).unmerged > 0 && (" - " + progress.find(pr => pr._id === level._id).unmerged)})
                                                </>
                                            )}
                                        </LevelName>
                                        {user.permission > 2 && (
                                            <MergeButton onClick={e => this.mergeLevel(e, level)} className="button">
                                                Merge
                                            </MergeButton>
                                        )}
                                        <StatusMarker levelStatus={status} onClick={(e) => this.handleLevelStatusChange(e, level._id, status)}>
                                            {translationLevelStatusLabels[status]}
                                        </StatusMarker>
                                        <Caret />
                                    </BlockHeader>
                                    {level.isOpen && (
                                        <BlockContent>
                                            {level.isFetching && (
                                                <LoaderWrapper>
                                                    <Loader />
                                                </LoaderWrapper>
                                            )}

                                            {!level.isFetching && (
                                                <TokenTable
                                                    tokens={level.tokens}
                                                    sourceLanguage={sourceLanguage}
                                                    saveToken={this.saveToken}
                                                    getToken={this.getToken}
                                                    users={users}
                                                    user={user}
                                                    approveTranslation={this.approveTranslation}
                                                    status={status}
                                                />
                                            )}
                                        </BlockContent>
                                    )}
                                </Block>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }
}

export default LanguagePage;
